import React, { useState, useCallback, useEffect } from 'react';
import {
  LTText,
  LTModal,
  LTIconButton,
  LTTag
} from '@lambdatestincprivate/lt-components';
import { Portal } from '@primer/react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XIcon
} from '@primer/octicons-react';
import { TagStyles } from './constants';
import styles from './AIRca.module.css';

// Image load lifecycle — drives the skeleton / fade-in / error placeholder.
const ImageStatus = {
  LOADING: 'loading',
  LOADED: 'loaded',
  ERROR: 'error'
};

const MediaPanel = ({ item, onEnlarge }) => {
  const [status, setStatus] = useState(ImageStatus.LOADING);

  const imageClassName =
    status === ImageStatus.LOADED
      ? `${styles.mediaImage} ${styles.mediaImageLoaded}`
      : styles.mediaImage;

  return (
    <div className={styles.mediaPanel}>
      <div className={styles.mediaPanelHeader}>
        <LTText variant={LTText.TextVariant.SUBHEADER_BOLD} color="text.muted">
          {item.label}
        </LTText>
      </div>
      {status === ImageStatus.ERROR && (
        <div className={styles.mediaImageWrap}>
          <div className={styles.mediaError}>
            <LTText
              variant={LTText.TextVariant.SMALL_REGULAR}
              color="text.muted"
            >
              Unable to load image
            </LTText>
          </div>
        </div>
      )}
      {status !== ImageStatus.ERROR && (
        <button
          type="button"
          className={styles.mediaImageWrap}
          onClick={onEnlarge}
          aria-label="Compare expected and actual screenshots"
          data-testid={`rca-media-enlarge-${item.label}`}
        >
          {status === ImageStatus.LOADING && (
            <div className={styles.mediaSkeleton} aria-hidden={true} />
          )}
          <img
            src={item.url}
            alt={`${item.label} screenshot`}
            className={imageClassName}
            loading="lazy"
            onLoad={() => setStatus(ImageStatus.LOADED)}
            onError={() => setStatus(ImageStatus.ERROR)}
          />
        </button>
      )}
    </div>
  );
};

/**
 * Media tab for an RCA timeline step. Shows the expected vs. actual
 * screenshots side by side, each with an enlarge control. The
 * enlarged comparison is titled with the step it belongs to (name + summary)
 * so the user keeps context while comparing.
 *
 * @param {object} props
 * @param {Array<{ label: string, url: string }>} props.media
 * @param {string} [props.stepName] - Timeline step name (modal title).
 * @param {string} [props.stepSummary] - Timeline step summary (modal subtitle).
 */
const RCAMediaTab = ({ media, stepName, stepSummary, mediaSteps, mediaIndex }) => {
  const [isEnlarged, setIsEnlarged] = useState(false);
  const [viewIndex, setViewIndex] = useState(0);

  // The enlarged view navigates across every media-bearing step. Fall back to
  // just this step when the cross-step list isn't provided.
  const steps =
    Array.isArray(mediaSteps) && mediaSteps.length > 0
      ? mediaSteps
      : [{ media, stepName, stepSummary }];
  const startIndex =
    typeof mediaIndex === 'number' && mediaIndex >= 0 ? mediaIndex : 0;

  const handleEnlarge = useCallback(() => {
    setViewIndex(startIndex);
    setIsEnlarged(true);
  }, [startIndex]);
  const handleClose = useCallback(() => setIsEnlarged(false), []);
  const goPrev = useCallback(() => setViewIndex((i) => Math.max(0, i - 1)), []);
  const goNext = useCallback(
    () => setViewIndex((i) => Math.min(steps.length - 1, i + 1)),
    [steps.length]
  );

  // Keyboard nav while the comparison is open: ←/↑ previous, →/↓ next.
  useEffect(() => {
    if (!isEnlarged) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isEnlarged, goPrev, goNext]);

  if (!Array.isArray(media) || media.length === 0) return null;

  const current = steps[viewIndex] || steps[0];
  const currentMedia = current?.media || [];
  const compareLabels = currentMedia.map((item) => item.label).join(' vs ');
  const showPager = steps.length > 1;

  return (
    <div className={styles.mediaGrid} data-testid="rca-media-grid">
      {media.map((item) => (
        <MediaPanel key={item.label} item={item} onEnlarge={handleEnlarge} />
      ))}

      {isEnlarged && (
        <Portal>
          <LTModal
            noPortal
            ariaLabel={
              current?.stepName
                ? `Screenshots for ${current.stepName} (${compareLabels})`
                : `RCA step screenshots (${compareLabels})`
            }
            customHeaderComponent={
              <div className={styles.mediaModalHeaderRow}>
                <div className={styles.mediaModalHeader}>
                  <div className={styles.mediaModalTitleRow}>
                    <span className={styles.mediaModalTitle}>
                      {current?.stepName || 'Step screenshots'}
                    </span>
                    {current?.tag && (
                      <LTTag
                        customStyle={TagStyles[current.tag]}
                        text={current.tag}
                      />
                    )}
                  </div>
                  {current?.stepSummary && (
                    <span className={styles.mediaModalSummary}>
                      {current.stepSummary}
                    </span>
                  )}
                </div>
                <div className={styles.mediaModalNav}>
                  {showPager && (
                    <>
                      <LTIconButton
                        icon={ChevronLeftIcon}
                        ariaLabel="Previous step screenshots"
                        variant={LTIconButton.IconButtonVariants.INVISIBLE}
                        size={LTIconButton.Size.SMALL}
                        disabled={viewIndex <= 0}
                        onClick={goPrev}
                      />
                      <span className={styles.mediaModalNavCount}>
                        {viewIndex + 1} / {steps.length}
                      </span>
                      <LTIconButton
                        icon={ChevronRightIcon}
                        ariaLabel="Next step screenshots"
                        variant={LTIconButton.IconButtonVariants.INVISIBLE}
                        size={LTIconButton.Size.SMALL}
                        disabled={viewIndex >= steps.length - 1}
                        onClick={goNext}
                      />
                      <span className={styles.mediaModalNavDivider} />
                    </>
                  )}
                  <LTIconButton
                    icon={XIcon}
                    ariaLabel="Close"
                    variant={LTIconButton.IconButtonVariants.INVISIBLE}
                    size={LTIconButton.Size.SMALL}
                    onClick={handleClose}
                  />
                </div>
              </div>
            }
            onClose={handleClose}
            extraStyle={{
              width: '1100px',
              maxWidth: '95vw',
              // Hide LTModal's built-in (Primer) close button; we render our own
              // inline with the step pager. This is the close button's DOM node
              // (LTModal also tags it data-testid="close-modal-button").
              '> button': { display: 'none !important' }
            }}
            data-testid="rca-media-compare-modal"
          >
            <div
              className={styles.mediaLightbox}
              style={{
                gridTemplateColumns: `repeat(${currentMedia.length}, minmax(0, 1fr))`
              }}
            >
              {currentMedia.map((item) => (
                <div key={item.label} className={styles.mediaLightboxItem}>
                  <LTText
                    variant={LTText.TextVariant.SUBHEADER_BOLD}
                    color="text.muted"
                  >
                    {item.label}
                  </LTText>
                  <div className={styles.mediaLightboxImageWrap}>
                    <img
                      src={item.url}
                      alt={`${item.label} screenshot`}
                      className={styles.mediaLightboxImage}
                    />
                  </div>
                </div>
              ))}
            </div>
          </LTModal>
        </Portal>
      )}
    </div>
  );
};

export default RCAMediaTab;
